import React, { useContext, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, AuthContext } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import TwitterVerificationNotice from './components/TwitterVerificationNotice';
import TwitterVerificationToast from './components/TwitterVerificationToast';
import GamerCat from './components/GamerCat';
import AnalyticsTracker from './components/AnalyticsTracker';
import ActivityModal from './components/ActivityModal';
import ToastContainer from './components/Toast/ToastContainer';
import CustomCursor from './components/CustomCursor';
import FeedbackNotice from './components/FeedbackNotice';
import './styles/accessibility.css';
import Home from './pages/Home';
import Login from './pages/Login';
import LoginSuccess from './pages/LoginSuccess';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import CommunityCards from './pages/CommunityCards';
import UpcomingReleases from './pages/UpcomingReleases';
import IndieGames from './pages/IndieGames';
import IndieGameDetail from './pages/IndieGameDetail';
import AdminDashboard from './pages/AdminDashboard';
import Tools from './pages/Tools';
import Giveaways from './pages/Giveaways';
import Discounts from './pages/Discounts';
import Profile from './pages/Profile';
import EditProfile from './pages/EditProfile';
import CreateShowcase from './pages/CreateShowcase';
import Feed from './pages/Feed';
import PostDetail from './pages/PostDetail';
import Messages from './pages/Messages';
import Scammers from './pages/Scammers';
import AddScammer from './pages/AddScammer';
import Settings from './pages/Settings';
import VerifyEmail from './pages/VerifyEmail';
import VerifyGiveaway from './pages/VerifyGiveaway';
import Leaderboard from './pages/Leaderboard';
import NotificationsPage from './pages/NotificationsPage';
import Shop from './pages/Shop';
import CodeRequests from './pages/CodeRequests';
import GameCodeRequests from './pages/GameCodeRequests';
import Achievements from './pages/Achievements';
import NewsPage from './components/NewsPage';
import PointsGuide from './pages/PointsGuide';
import GameReviews from './pages/GameReviews';
import About from './pages/About';
import FAQ from './pages/FAQ';
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import AffiliateDisclosure from './pages/AffiliateDisclosure';
import Contact from './pages/Contact';
import TwitterVerification from './pages/TwitterVerification';
import TwitterAccountIdFetcher from './pages/TwitterAccountIdFetcher';
import Chat from './pages/Chat';
import TwitterWinnerPicker from './pages/TwitterWinnerPicker';
import TwitterToolsHub from './pages/TwitterToolsHub';
import CreatorToolsHub from './pages/CreatorToolsHub';
import YouTubeCommentPicker from './pages/YouTubeCommentPicker';
import TwitchPicker from './pages/TwitchPicker';
import DiscoverLists from './pages/DiscoverLists';
import CreateGameList from './pages/CreateGameList';
import EditGameList from './pages/EditGameList';
import GameListDetails from './pages/GameListDetails';
import Partners from './pages/Partners';
import HallOfFame from './pages/HallOfFame';
import pingService from './utils/pingService';

// Protected Route Component
const ProtectedRoute = ({ children, adminOnly = false, indieDevOnly = false }) => {
  const { isAuthenticated, isAdmin, isIndieDev, loading } = useContext(AuthContext);

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  if (adminOnly && !isAdmin) {
    return <Navigate to="/dashboard" />;
  }

  if (indieDevOnly && !isIndieDev) {
    return <Navigate to="/dashboard" />;
  }

  return children;
};

function AppContent() {
  // Start ping service to keep server alive on Render
  useEffect(() => {
    pingService.start();

    // Cleanup on unmount
    return () => {
      pingService.stop();
    };
  }, []);

  return (
    <Router>
      <div className="App">
        <CustomCursor />
        <Navbar />
        <AnalyticsTracker />
        <TwitterVerificationNotice />
        <FeedbackNotice />
        <TwitterVerificationToast />
        <GamerCat />
        <ActivityModal />
        <ToastContainer />
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/login" element={<Login />} />
          <Route path="/login-success" element={<LoginSuccess />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password/:token" element={<ResetPassword />} />
          <Route path="/verify-email/:token" element={<VerifyEmail />} />
          <Route path="/verify" element={<VerifyGiveaway />} />
          <Route path="/community" element={<CommunityCards />} />
          <Route path="/upcoming-releases" element={<UpcomingReleases />} />
          <Route path="/discover-lists" element={<DiscoverLists />} />
          <Route 
            path="/create-list" 
            element={
              <ProtectedRoute>
                <CreateGameList />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/lists/:id/edit" 
            element={
              <ProtectedRoute>
                <EditGameList />
              </ProtectedRoute>
            } 
          />
          <Route path="/lists/:id" element={<GameListDetails />} />
          <Route path="/indie-games" element={<IndieGames />} />
          <Route path="/indie-games/:id" element={<IndieGameDetail />} />
          <Route path="/tools" element={<Tools />} />
          <Route path="/posts/:postId" element={<PostDetail />} />
          <Route path="/giveaways" element={<Giveaways />} />
          <Route path="/giveaways/:id" element={<Giveaways />} />
          <Route path="/discounts" element={<Discounts />} />
          <Route path="/code-requests" element={<CodeRequests />} />
          <Route path="/code-requests/game/:gameName" element={<GameCodeRequests />} />
          <Route path="/leaderboard" element={<Leaderboard />} />
          <Route path="/news" element={<NewsPage />} />
          <Route path="/points-guide" element={<PointsGuide />} />
          <Route path="/game-reviews" element={<GameReviews />} />
          <Route path="/scammers" element={<Scammers />} />
          <Route path="/about" element={<About />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:id" element={<BlogPost />} />
          <Route path="/twitter-verification" element={<TwitterVerification />} />
          <Route path="/twitter-account-id" element={<TwitterAccountIdFetcher />} />
          <Route path="/twitter-winner-picker" element={<TwitterWinnerPicker />} />
          <Route path="/twitter-tools" element={<TwitterToolsHub />} />
          <Route path="/creator-tools" element={<CreatorToolsHub />} />
          <Route path="/youtube-comment-picker" element={<YouTubeCommentPicker />} />
          <Route path="/twitch-picker" element={<TwitchPicker />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/affiliate-disclosure" element={<AffiliateDisclosure />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/partners" element={<Partners />} />
          <Route path="/hall-of-fame" element={<HallOfFame />} />
          <Route
            path="/achievements"
            element={
              <ProtectedRoute>
                <Achievements />
              </ProtectedRoute>
            }
          />
          <Route
            path="/shop"
            element={
              <ProtectedRoute>
                <Shop />
              </ProtectedRoute>
            }
          />
          <Route
            path="/scammers/add"
            element={
              <ProtectedRoute>
                <AddScammer />
              </ProtectedRoute>
            }
          />
          <Route path="/profile/:username" element={<Profile />} />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <Settings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/feed"
            element={
              <ProtectedRoute>
                <Feed />
              </ProtectedRoute>
            }
          />
          <Route
            path="/messages"
            element={
              <ProtectedRoute>
                <Messages />
              </ProtectedRoute>
            }
          />
          <Route path="/chat" element={<Chat />} />
          <Route
            path="/notifications"
            element={
              <ProtectedRoute>
                <NotificationsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile/edit"
            element={
              <ProtectedRoute>
                <EditProfile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/showcase/create"
            element={
              <ProtectedRoute>
                <CreateShowcase />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <ProtectedRoute adminOnly={true}>
                <AdminDashboard />
              </ProtectedRoute>
            }
          />
        </Routes>
        <Footer />
      </div>
    </Router>
  );
}

function App() {
  return (
    <AuthProvider>
      <ThemeProvider>
        <AppContent />
      </ThemeProvider>
    </AuthProvider>
  );
}

export default App;
